import { useState, useEffect } from "react";
import { API, formatApiErrorDetail } from "../../App";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../components/ui/table";
import { Mail, Inbox, Search, Download, Trash2, CheckCircle2, RotateCcw } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

export default function AdminInquiries() {
  const [tab, setTab] = useState("inquiries");
  const [inquiries, setInquiries] = useState([]);
  const [subscribers, setSubscribers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [showHandled, setShowHandled] = useState(false);

  useEffect(() => {
    fetchAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, showHandled]);

  const fetchAll = async () => {
    setLoading(true);
    try {
      if (tab === "inquiries") {
        const params = showHandled ? "" : "?is_handled=false";
        const res = await axios.get(`${API}/admin/inquiries${params}`);
        setInquiries(res.data);
      } else {
        const res = await axios.get(`${API}/admin/newsletter-subscribers`);
        setSubscribers(res.data);
      }
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed to load");
    } finally {
      setLoading(false);
    }
  };

  const handleToggleHandled = async (inq) => {
    const next = !inq.is_handled;
    try {
      await axios.put(`${API}/admin/inquiries/${inq.inquiry_id}`, { is_handled: next });
      setInquiries((prev) =>
        prev
          .map((x) => (x.inquiry_id === inq.inquiry_id ? { ...x, is_handled: next } : x))
          .filter((x) => (showHandled ? true : !x.is_handled)),
      );
      toast.success(next ? "Marked handled" : "Reopened");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed");
    }
  };

  const handleDeleteInquiry = async (inq) => {
    if (!window.confirm(`Delete inquiry from ${inq.name}?`)) return;
    try {
      await axios.delete(`${API}/admin/inquiries/${inq.inquiry_id}`);
      setInquiries((prev) => prev.filter((x) => x.inquiry_id !== inq.inquiry_id));
      toast.success("Deleted");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed");
    }
  };

  const handleUnsubscribe = async (sub) => {
    if (!window.confirm(`Unsubscribe ${sub.email}?`)) return;
    try {
      await axios.delete(`${API}/admin/newsletter-subscribers/${encodeURIComponent(sub.email)}`);
      setSubscribers((prev) => prev.filter((x) => x.email !== sub.email));
      toast.success("Unsubscribed");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed");
    }
  };

  const handleExport = () => {
    const url = tab === "inquiries"
      ? `${API}/admin/inquiries/export.csv`
      : `${API}/admin/newsletter-subscribers/export.csv`;
    window.open(url, "_blank");
  };

  const filteredInquiries = inquiries.filter((i) => {
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    return (
      i.name?.toLowerCase().includes(q) ||
      i.email?.toLowerCase().includes(q) ||
      i.venue_name?.toLowerCase().includes(q) ||
      i.message?.toLowerCase().includes(q)
    );
  });
  const filteredSubs = subscribers.filter((s) =>
    !search.trim() ? true : s.email?.toLowerCase().includes(search.toLowerCase()),
  );

  return (
    <div data-testid="admin-inquiries-page">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-medium text-foreground flex items-center gap-2"><Inbox className="h-6 w-6 text-primary" />Inquiries & Subscribers</h1>
        <Button onClick={handleExport} variant="outline" data-testid="inquiries-export-btn">
          <Download className="h-4 w-4 mr-2" /> Export CSV
        </Button>
      </div>

      <div className="flex gap-2 mb-4 flex-wrap">
        <Button
          variant={tab === "inquiries" ? "default" : "outline"}
          size="sm"
          onClick={() => setTab("inquiries")}
          data-testid="tab-inquiries"
        >
          <Inbox className="h-4 w-4 mr-2" /> Contact inquiries ({inquiries.length})
        </Button>
        <Button
          variant={tab === "subscribers" ? "default" : "outline"}
          size="sm"
          onClick={() => setTab("subscribers")}
          data-testid="tab-subscribers"
        >
          <Mail className="h-4 w-4 mr-2" /> Newsletter subscribers ({subscribers.length})
        </Button>
      </div>

      <div className="flex gap-3 mb-4 flex-wrap">
        <div className="relative flex-1 min-w-[260px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder={tab === "inquiries" ? "Search by name, email, venue…" : "Search email…"}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-10"
            data-testid="inquiries-search"
          />
        </div>
        {tab === "inquiries" && (
          <Button
            variant={showHandled ? "default" : "outline"}
            size="sm"
            onClick={() => setShowHandled(!showHandled)}
            data-testid="toggle-show-handled"
          >
            {showHandled ? "Showing all" : "Showing open only"}
          </Button>
        )}
      </div>

      {loading ? (
        <div className="animate-pulse bg-muted h-64 rounded-xl" />
      ) : tab === "inquiries" ? (
        filteredInquiries.length === 0 ? (
          <div className="bg-card rounded-xl border border-border p-8 text-center">
            <Inbox className="h-12 w-12 text-muted-foreground/40 mx-auto mb-3" />
            <p className="text-muted-foreground">No inquiries{showHandled ? "" : " open"} right now.</p>
          </div>
        ) : (
          <div className="bg-card rounded-xl border border-border overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Received</TableHead>
                  <TableHead>From</TableHead>
                  <TableHead>Venue</TableHead>
                  <TableHead>Plan</TableHead>
                  <TableHead>Message</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredInquiries.map((inq) => (
                  <TableRow
                    key={inq.inquiry_id}
                    className={inq.is_handled ? "opacity-60" : ""}
                    data-testid={`inquiry-row-${inq.inquiry_id}`}
                  >
                    <TableCell className="text-xs text-muted-foreground">
                      {inq.created_at?.slice(0, 16).replace("T", " ")}
                    </TableCell>
                    <TableCell>
                      <div className="font-medium">{inq.name}</div>
                      <a href={`mailto:${inq.email}`} className="text-xs text-primary hover:underline">
                        {inq.email}
                      </a>
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">{inq.venue_name || "—"}</TableCell>
                    <TableCell className="text-sm text-muted-foreground">{inq.plan_interest || "—"}</TableCell>
                    <TableCell className="max-w-md">
                      <p className="text-sm line-clamp-2 whitespace-pre-wrap" title={inq.message}>
                        {inq.message}
                      </p>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleToggleHandled(inq)}
                          title={inq.is_handled ? "Reopen" : "Mark handled"}
                          data-testid={`inquiry-toggle-${inq.inquiry_id}`}
                        >
                          {inq.is_handled ? (
                            <RotateCcw className="h-3.5 w-3.5" />
                          ) : (
                            <CheckCircle2 className="h-3.5 w-3.5 text-green-600" />
                          )}
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDeleteInquiry(inq)}
                          data-testid={`inquiry-delete-${inq.inquiry_id}`}
                        >
                          <Trash2 className="h-3.5 w-3.5 text-muted-foreground hover:text-destructive" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )
      ) : filteredSubs.length === 0 ? (
        <div className="bg-card rounded-xl border border-border p-8 text-center">
          <Mail className="h-12 w-12 text-muted-foreground/40 mx-auto mb-3" />
          <p className="text-muted-foreground">No newsletter subscribers yet.</p>
        </div>
      ) : (
        <div className="bg-card rounded-xl border border-border overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Email</TableHead>
                <TableHead>Subscribed</TableHead>
                <TableHead>Source</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredSubs.map((sub) => (
                <TableRow key={sub.email} data-testid={`subscriber-row-${sub.email}`}>
                  <TableCell className="font-medium">{sub.email}</TableCell>
                  <TableCell className="text-xs text-muted-foreground">
                    {sub.subscribed_at?.slice(0, 10)}
                  </TableCell>
                  <TableCell className="text-sm text-muted-foreground">{sub.last_source || "—"}</TableCell>
                  <TableCell className="text-right">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleUnsubscribe(sub)}
                      data-testid={`unsubscribe-${sub.email}`}
                    >
                      <Trash2 className="h-3.5 w-3.5 text-muted-foreground hover:text-destructive" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
}
